import { useTranslation } from 'react-i18next';
import type { CjmStage, MethodContent } from '@/types/methodologies';

interface CjmViewProps {
  /** Unused here — CJM stages carry no per-methodology section keys to translate
   * — kept so CjmView has the same prop shape as the other registered views. */
  methodId: string;
  content: MethodContent;
}

const EMOTION_FACE: Record<CjmStage['emotion'], string> = {
  [-2]: '😞',
  [-1]: '🙁',
  [0]: '😐',
  [1]: '🙂',
  [2]: '😄',
};

const EMOTION_BG: Record<CjmStage['emotion'], string> = {
  [-2]: 'bg-[#fdeceb]',
  [-1]: 'bg-[#fdf3e3]',
  [0]: 'bg-[#f4f4f3]',
  [1]: 'bg-[rgba(33,151,241,0.08)]',
  [2]: 'bg-[#eaf7ee]',
};

function ItemList({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-col gap-[6px]">
      {items.map((item, index) => (
        <li key={index} className="font-segoe text-[13px] leading-[18px] text-[#0d0d0d]">
          {item}
        </li>
      ))}
    </ul>
  );
}

/**
 * Stages as columns, attributes as rows — the table scrolls horizontally
 * inside its own container so a long journey never widens the page. The
 * label column stays pinned while the stages scroll under it.
 */
export function CjmView({ content }: CjmViewProps) {
  const { t } = useTranslation('methodologies');
  const stages = content.stages ?? [];

  const rowLabelClass = 'sticky left-0 z-10 bg-white font-manrope font-semibold text-[13px] leading-[18px] text-[#565858] uppercase align-top p-[16px] w-[140px] min-w-[140px]';
  const cellClass = 'align-top p-[16px] w-[220px] min-w-[220px] border-l border-[#eeeeee]';

  return (
    <div className="overflow-x-auto rounded-[16px] border border-[#eeeeee]">
      <table className="border-collapse w-full">
        <thead>
          <tr className="bg-[#f4f4f3]">
            <th className={`${rowLabelClass} bg-[#f4f4f3]`} />
            {stages.map((stage, index) => (
              <th key={index} className={`${cellClass} text-left`}>
                <p className="font-segoe text-[12px] leading-[16px] text-[#969696] mb-[4px]">
                  {t('cjm.stage', { number: index + 1 })}
                </p>
                <p className="font-manrope font-bold text-[16px] leading-[22px] text-[#0d0d0d]">
                  {stage.name}
                </p>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-[#eeeeee]">
            <td className={rowLabelClass}>{t('cjm.goal')}</td>
            {stages.map((stage, index) => (
              <td key={index} className={cellClass}>
                <p className="font-segoe text-[13px] leading-[18px] text-[#0d0d0d]">{stage.goal}</p>
              </td>
            ))}
          </tr>
          <tr className="border-t border-[#eeeeee]">
            <td className={rowLabelClass}>{t('cjm.actions')}</td>
            {stages.map((stage, index) => (
              <td key={index} className={cellClass}><ItemList items={stage.actions} /></td>
            ))}
          </tr>
          <tr className="border-t border-[#eeeeee]">
            <td className={rowLabelClass}>{t('cjm.touchpoints')}</td>
            {stages.map((stage, index) => (
              <td key={index} className={cellClass}><ItemList items={stage.touchpoints} /></td>
            ))}
          </tr>
          <tr className="border-t border-[#eeeeee]">
            <td className={rowLabelClass}>{t('cjm.emotion')}</td>
            {stages.map((stage, index) => (
              <td key={index} className={cellClass}>
                <div className={`inline-flex items-center gap-[8px] rounded-[8px] px-[8px] py-[4px] ${EMOTION_BG[stage.emotion]}`}>
                  <span aria-hidden="true">{EMOTION_FACE[stage.emotion]}</span>
                  <span className="font-segoe font-semibold text-[13px] text-[#0d0d0d]">{stage.emotion}</span>
                </div>
                <p className="font-segoe text-[13px] leading-[18px] text-[#565858] mt-[8px]">{stage.emotionNote}</p>
              </td>
            ))}
          </tr>
          <tr className="border-t border-[#eeeeee]">
            <td className={rowLabelClass}>{t('cjm.pains')}</td>
            {stages.map((stage, index) => (
              <td key={index} className={cellClass}><ItemList items={stage.pains} /></td>
            ))}
          </tr>
          <tr className="border-t border-[#eeeeee]">
            <td className={rowLabelClass}>{t('cjm.opportunities')}</td>
            {stages.map((stage, index) => (
              <td key={index} className={cellClass}><ItemList items={stage.opportunities} /></td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
